import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, Save, Trash2, ExternalLink, Calculator, Edit2, Check, X as XIcon, Building2, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { EquipmentSelector } from './EquipmentSelector';
import { ScenarioSummary } from './ScenarioSummary';
import { ImprovementsList } from './ImprovementsList';
import { WorksSynthesisTable } from './WorksSynthesisTable';
import { PricingInfoCard } from './PricingInfoCard';
import { FinancialAidCard } from './FinancialAidCard';
import { GES2026InfoCard } from './GES2026InfoCard';
import { ScenarioPDFDownloadButton } from '../pdf/ScenarioPDFDownloadButton';
import { ScenarioOptimizer } from './ScenarioOptimizer';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
  fetchScenariosByDPE,
  fetchScenarioWithImprovements,
  createWorkScenario,
  deleteScenario,
  updateScenarioStatus,
  updateScenarioName,
} from '../../services/work-scenarios-service';
import {
  createAndApplyOptimizedScenario,
} from '../../services/scenario-optimizer-service';
import type { WorkScenario, ScenarioWithImprovements } from '../../types/work-scenarios';
import type { OptimizationStrategy } from '../../utils/scenario-optimizer';
import { useToast } from '../ui/Toast';
import { isProSubscription } from '../../stripe-config';

export function WorkScenariosPage() {
  const { id: dpeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  // Refs pour éviter les problèmes de closure dans useCallback
  const dpeIdRef = useRef(dpeId);
  const selectedScenarioRef = useRef<ScenarioWithImprovements | null>(null);

  const [scenarios, setScenarios] = useState<WorkScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<ScenarioWithImprovements | null>(null);
  const [dpeData, setDpeData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showEquipmentSelector, setShowEquipmentSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingScenarioId, setEditingScenarioId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [optimizationMessage, setOptimizationMessage] = useState('');
  const optimizationInProgressRef = useRef(false);

  // Synchroniser les refs avec les états
  useEffect(() => {
    dpeIdRef.current = dpeId;
  }, [dpeId]);

  useEffect(() => {
    selectedScenarioRef.current = selectedScenario;
  }, [selectedScenario]);

  useEffect(() => {
    if (dpeId && user?.id) {
      loadDPEData();
      loadScenarios();
    }
  }, [dpeId, user?.id]);

  async function loadDPEData() {
    if (!dpeId || !user?.id) return;

    try {
      const { data, error: dpeError } = await supabase
        .from('dpe_data')
        .select('*')
        .eq('id', dpeId)
        .eq('user_id', user.id)
        .single();

      if (dpeError || !data) {
        throw new Error('DPE non trouvé ou accès non autorisé');
      }

      setDpeData(data);
    } catch (err) {
      console.error('Error loading DPE data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load DPE data');
    }
  }

  async function loadScenarios() {
    if (!dpeId) return;

    try {
      setIsLoading(true);
      const data = await fetchScenariosByDPE(dpeId);
      setScenarios(data);

      if (data.length > 0 && !selectedScenarioRef.current) {
        await loadScenarioDetails(data[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setIsLoading(false);
    }
  }

  async function loadScenarioDetails(scenarioId: string) {
    try {
      const data = await fetchScenarioWithImprovements(scenarioId);
      setSelectedScenario(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenario details');
    }
  }

  async function handleCreateScenario() {
    if (!dpeId) return;

    try {
      setIsCreating(true);
      const newScenario = await createWorkScenario({
        dpe_id: dpeId,
        name: `Scénario ${scenarios.length + 1}`,
      });
      await loadScenarios();
      await loadScenarioDetails(newScenario.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create scenario');
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDeleteScenario(scenarioId: string) {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce scénario ?')) {
      return;
    }

    try {
      await deleteScenario(scenarioId);
      await loadScenarios();
      if (selectedScenarioRef.current?.id === scenarioId) {
        setSelectedScenario(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scenario');
    }
  }

  async function handleSaveScenario() {
    if (!selectedScenarioRef.current) return;

    try {
      await updateScenarioStatus(selectedScenarioRef.current.id, 'saved');
      await loadScenarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scenario');
    }
  }

  function startEditingScenarioName(scenario: WorkScenario) {
    setEditingScenarioId(scenario.id);
    setEditingName(scenario.name);
  }

  async function saveScenarioName() {
    if (!editingScenarioId || !editingName.trim()) {
      return;
    }

    try {
      await updateScenarioName(editingScenarioId, editingName.trim());
      setEditingScenarioId(null);
      setEditingName('');
      await loadScenarios();
      if (selectedScenarioRef.current?.id === editingScenarioId) {
        await loadScenarioDetails(editingScenarioId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update scenario name');
    }
  }

  function cancelEditingScenarioName() {
    setEditingScenarioId(null);
    setEditingName('');
  }

  function handleImprovementAdded() {
    if (selectedScenarioRef.current) {
      loadScenarioDetails(selectedScenarioRef.current.id);
      loadScenarios();
    }
    setShowEquipmentSelector(false);
  }

  const handleSelectStrategy = useCallback(async (strategy: OptimizationStrategy) => {
    const currentDpeId = dpeIdRef.current;
    if (!currentDpeId) return;
    if (optimizationInProgressRef.current) return;

    try {
      optimizationInProgressRef.current = true;
      setOptimizationProgress(0);
      setOptimizationMessage('Initialisation...');

      const newScenarioId = await createAndApplyOptimizedScenario(
        currentDpeId,
        strategy,
        (progress, message) => {
          setOptimizationProgress(progress);
          setOptimizationMessage(message);
        }
      );

      await loadScenarios();
      await loadScenarioDetails(newScenarioId);

      showToast({
        type: 'success',
        title: 'Scénario généré',
        message: 'Le scénario optimisé a été créé avec succès.',
        duration: 5000
      });

    } catch (err) {
      console.error('Optimization failed:', err);
      showToast({
        type: 'error',
        title: 'Erreur lors de l\'optimisation',
        message: err instanceof Error ? err.message : 'Erreur inconnue',
        duration: 8000
      });
    } finally {
      optimizationInProgressRef.current = false;
      setOptimizationProgress(0);
      setOptimizationMessage('');
    }
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(`/analysis/${dpeId}/results`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux résultats
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Scénarios de travaux
              </h1>
              <p className="mt-2 text-gray-600">
                Simulez l'impact de différents travaux de rénovation sur votre DPE
              </p>
            </div>
            <div className="flex items-center gap-3">
              {selectedScenario && selectedScenario.improvements.length > 0 && dpeData && (
                <ScenarioPDFDownloadButton
                  scenario={selectedScenario}
                  dpeData={dpeData}
                  variant="outline"
                  size="sm"
                />
              )}
              <Button onClick={handleCreateScenario} disabled={isCreating}>
                <Plus className="w-5 h-5 mr-2" />
                Nouveau scénario
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <Card className="p-4">
              <h2 className="font-semibold text-gray-900 mb-4">Mes scénarios</h2>
              <div className="space-y-2">
                {scenarios.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <p className="text-gray-600 text-sm mb-4">
                      Aucun scénario pour le moment
                    </p>
                    <p className="text-gray-500 text-xs">
                      Utilisez les options ci-contre pour générer votre premier scénario
                    </p>
                  </div>
                ) : (
                  scenarios.map((scenario) => (
                  <div
                    key={scenario.id}
                    className={`w-full p-3 rounded-lg transition-colors ${
                      selectedScenario?.id === scenario.id
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    {editingScenarioId === scenario.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveScenarioName();
                            if (e.key === 'Escape') cancelEditingScenarioName();
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={saveScenarioName}
                            className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center justify-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Valider
                          </button>
                          <button
                            onClick={cancelEditingScenarioName}
                            className="flex-1 px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400 flex items-center justify-center gap-1"
                          >
                            <XIcon className="w-3 h-3" />
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => loadScenarioDetails(scenario.id)}
                        className="w-full cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            loadScenarioDetails(scenario.id);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{scenario.name}</div>
                            <div className="text-sm text-gray-500">
                              {scenario.projected_energy_class
                                ? `→ Classe ${scenario.projected_energy_class}`
                                : 'Aucune amélioration'}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingScenarioName(scenario);
                              }}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Renommer"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteScenario(scenario.id);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  ))
                )}
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Calculator className="w-5 h-5 text-green-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-2">
                    Chiffrez vos travaux
                  </h3>
                  <p className="text-sm text-green-800 mb-3">
                    Utilisez SimulRéno pour obtenir une estimation précise du coût de vos travaux sélectionnés
                  </p>

                    <a href="https://www.simulreno.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-green-700 hover:text-green-900 transition-colors"
                  >
                    Accéder à SimulRéno
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </Card>

            {/* Upsell Pro Agence — masqué pour les abonnés Pro */}
            {!isProSubscription(user?.subscription_price_id) && (
              <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                    <Building2 className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 mb-1">
                      Vous êtes professionnel ?
                    </h3>
                    <p className="text-sm text-blue-800 mb-3">
                      Envoyez ce rapport PDF à vos clients avec <strong>votre logo et votre nom d'agence</strong>.
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-blue-700 mb-3">
                      <FileText className="w-3.5 h-3.5" />
                      <span>PDF white-label · 99€ HT/mois</span>
                    </div>
                    <button
                      onClick={() => navigate('/pro')}
                      className="text-sm font-medium text-blue-700 hover:text-blue-900 underline underline-offset-2 transition-colors"
                    >
                      Découvrir le plan Pro Agence →
                    </button>
                  </div>
                </div>
              </Card>
            )}

{scenarios.length > 0 && selectedScenario && dpeId && (
              <Card className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Générer un scénario</h3>
                <ScenarioOptimizer
                  compact={true}
                  onSelectStrategy={handleSelectStrategy}
                  onSelectCustom={() => {
                    handleCreateScenario();
                    setShowEquipmentSelector(true);
                  }}
                  isLoading={optimizationInProgressRef.current}
                  progress={optimizationProgress}
                  progressMessage={optimizationMessage}
                  currentStrategy={selectedScenario.optimization_strategy as OptimizationStrategy}
                />
              </Card>
            )}
          </div>

          <div className="lg:col-span-3">
            {scenarios.length === 0 ? (
              <div className="space-y-6">
                
                {dpeId && (
                  <ScenarioOptimizer
                    onSelectStrategy={handleSelectStrategy}
                    onSelectCustom={() => {
                      handleCreateScenario();
                      setShowEquipmentSelector(true);
                    }}
                    isLoading={optimizationInProgressRef.current}
                    progress={optimizationProgress}
                    progressMessage={optimizationMessage}
                  />
                )}
              </div>
            ) : selectedScenario ? (
              <div className="space-y-6">
                {selectedScenario.improvements.length === 0 && dpeId && (
                  <ScenarioOptimizer
                    onSelectStrategy={handleSelectStrategy}
                    onSelectCustom={() => setShowEquipmentSelector(true)}
                    isLoading={optimizationInProgressRef.current}
                    progress={optimizationProgress}
                    progressMessage={optimizationMessage}
                  />
                )}

                <ScenarioSummary scenario={selectedScenario} dpeData={dpeData} />

                {selectedScenario.improvements.length > 0 ? (
                  <>
                    <Card className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">
                          Travaux planifiés
                        </h2>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowEquipmentSelector(true)}
                            variant="outline"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter des travaux
                          </Button>
                          {selectedScenario.status === 'draft' && (
                            <Button onClick={handleSaveScenario}>
                              <Save className="w-4 h-4 mr-2" />
                              Enregistrer
                            </Button>
                          )}
                        </div>
                      </div>

                      <ImprovementsList
                        improvements={selectedScenario.improvements}
                        onDelete={() => loadScenarioDetails(selectedScenario.id)}
                      />
                    </Card>

                    <WorksSynthesisTable improvements={selectedScenario.improvements} />

                    <GES2026InfoCard />

                    <PricingInfoCard />

                    <FinancialAidCard />
                  </>
                ) : (
                  <Card className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Plus className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Commencez votre scénario
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Ajoutez des travaux de rénovation pour simuler leur impact sur votre DPE et calculer les économies d'énergie potentielles.
                      </p>
                      <Button
                        onClick={() => setShowEquipmentSelector(true)}
                        size="lg"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Ajouter des travaux
                      </Button>
                    </div>
                  </Card>
                )}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <p className="text-gray-500">
                  Sélectionnez ou créez un scénario pour commencer
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {showEquipmentSelector && selectedScenario && dpeId && (
        <EquipmentSelector
          scenarioId={selectedScenario.id}
          dpeId={dpeId}
          onClose={() => setShowEquipmentSelector(false)}
          onImprovementAdded={handleImprovementAdded}
        />
      )}
    </div>
  );
}